import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import LogoSlashSVG from './LogoSlashSVG';
import useReducedMotion from '../../hooks/useReducedMotion';

/**
 * Full-screen animated intro (~5.0–5.2s).
 *
 * SINGLE "\\" — placed in the wordmark row from the start, kept still while
 * ARAIA and DESIGN slide in from the sides.
 *
 *   0.0 → 0.9s: // draws in (clip-path bars reveal). ARAIA and DESIGN are off-screen.
 *   1.0 → 2.0s: ARAIA slides from left (-40vw → 0), DESIGN slides from right (40vw → 0).
 *   2.3 → 3.3s: claim TWO VISION, ONE CREATIVE DIRECTION. fades in.
 *   3.3 → 4.3s: DWELL — everything holds still, fully legible.
 *   4.3 → 5.2s: exit — wordmark slides out, // grows into a subtle accent, stage fades away.
 *
 * Skip listeners arm only after 600ms so ambient events during load don't dismiss it.
 */
export default function IntroSequence({ onComplete }) {
    const reduced = useReducedMotion();

    const stageRef = useRef(null);
    const slashSvgRef = useRef(null);
    const slashCenterRef = useRef(null);
    const araiaRef = useRef(null);
    const designRef = useRef(null);
    const claimRef = useRef(null);
    const hintRef = useRef(null);

    const tlRef = useRef(null);
    const finishedRef = useRef(false);
    const skipArmedRef = useRef(false);

    const finish = () => {
        if (finishedRef.current) return;
        finishedRef.current = true;
        tlRef.current?.kill();
        onComplete?.();
    };

    useEffect(() => {
        if (reduced) {
            const t = setTimeout(finish, 200);
            return () => clearTimeout(t);
        }

        const tl = gsap.timeline({
            defaults: { ease: 'power3.out' },
            onComplete: finish,
        });
        tlRef.current = tl;

        const bars = slashSvgRef.current?.querySelectorAll('.intro-slash-bar');

        // T=0 → T=0.9s: single // draws in — BOTH bars simultaneously (no stagger)
        // so it never looks like "one \\ rising with a second one coming from below"
        if (bars && bars.length) {
            tl.to(
                bars,
                {
                    clipPath: 'inset(0% 0 0 0)',
                    duration: 0.85,
                    ease: 'power3.out',
                },
                0,
            );
        }
        // Skip hint fade-in
        tl.to(hintRef.current, { opacity: 1, duration: 0.6 }, 0.4);

        // T=1.0s → T=2.0s: ARAIA slides in from left, DESIGN from right
        // The // stays perfectly still throughout (it's a flex sibling in the row).
        tl.to(
            araiaRef.current,
            { opacity: 1, x: '0vw', duration: 0.95, ease: 'power3.out' },
            1.0,
        );
        tl.to(
            designRef.current,
            { opacity: 1, x: '0vw', duration: 0.95, ease: 'power3.out' },
            1.0,
        );

        // T=2.3s → T=3.3s: claim fade-in
        tl.to(
            claimRef.current,
            { opacity: 1, duration: 1.0, ease: 'power2.out' },
            2.3,
        );

        // T=3.3s → T=4.3s: DWELL
        tl.to({}, { duration: 1.0 }, 3.3);

        // T=4.3s → T=5.2s: exit
        tl.to(
            araiaRef.current,
            { x: '-120vw', opacity: 0, duration: 0.9, ease: 'power3.inOut' },
            4.3,
        );
        tl.to(
            designRef.current,
            { x: '120vw', opacity: 0, duration: 0.9, ease: 'power3.inOut' },
            4.3,
        );
        tl.to(
            claimRef.current,
            { opacity: 0, duration: 0.6, ease: 'power2.inOut' },
            4.35,
        );
        tl.to(hintRef.current, { opacity: 0, duration: 0.4 }, 4.2);
        // Single // grows into a subtle vertical accent then fades
        tl.to(
            slashCenterRef.current,
            {
                scaleY: 8,
                scaleX: 0.35,
                opacity: 0,
                duration: 0.9,
                ease: 'power3.inOut',
                transformOrigin: 'center center',
            },
            4.3,
        );
        // Stage fade-out → reveal home beneath
        tl.to(
            stageRef.current,
            { opacity: 0, duration: 0.75, ease: 'power2.inOut' },
            4.55,
        );

        return () => {
            tl.kill();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reduced]);

    const handleSkip = (e) => {
        if (!skipArmedRef.current) return;
        e?.preventDefault?.();
        finish();
    };

    useEffect(() => {
        if (reduced) return;
        const armDelay = setTimeout(() => {
            skipArmedRef.current = true;
        }, 600);

        const onSkip = () => {
            if (skipArmedRef.current) finish();
        };
        window.addEventListener('wheel', onSkip, { passive: true });
        window.addEventListener('touchmove', onSkip, { passive: true });
        window.addEventListener('keydown', onSkip);
        return () => {
            clearTimeout(armDelay);
            window.removeEventListener('wheel', onSkip);
            window.removeEventListener('touchmove', onSkip);
            window.removeEventListener('keydown', onSkip);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reduced]);

    return (
        <div
            className="intro-stage"
            ref={stageRef}
            onClick={handleSkip}
            data-testid="intro-stage"
            role="button"
            aria-label="Intro — click or scroll to skip"
        >
            <div className="intro-inner">
                {/* Wordmark row — flexbox with the // as the fixed center child.
                    ARAIA and DESIGN start off-screen (via inline transform) and slide in.
                    The // stays put throughout. */}
                <div className="intro-wordmark-row">
                    <span
                        className="intro-word intro-word--left"
                        ref={araiaRef}
                        data-testid="intro-word-araia"
                    >
                        ARAIA
                    </span>
                    <span
                        className="intro-slash-center slash-mark"
                        ref={slashCenterRef}
                        aria-hidden="true"
                        data-testid="intro-slash-center"
                    >
                        <LogoSlashSVG ref={slashSvgRef} />
                    </span>
                    <span
                        className="intro-word intro-word--right"
                        ref={designRef}
                        data-testid="intro-word-design"
                    >
                        DESIGN
                    </span>
                </div>

                {/* Claim — literal uppercase in DOM so innerText matches TWO VISION across browsers */}
                <div className="intro-claim" ref={claimRef} data-testid="intro-claim">
                    TWO VISION, ONE CREATIVE DIRECTION.
                </div>
            </div>

            <div className="intro-skip-hint" ref={hintRef} data-testid="intro-skip-hint">
                Click or scroll to enter
            </div>
        </div>
    );
}
